import React, { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import '../../styles/inputs-Css/ActivityLog.css';
import Pagination from '../../components/common/Pagination.js';
import { getTimezoneFormattedDate } from '../../utils/dateUtils.js';
import SearchBar from '../../components/common/SearchBar.js';

const formatLogDetails = (action, detailsString) => {
  let details;
  try {
    details = JSON.parse(detailsString);
  } catch (e) {
    return detailsString || 'No additional details.';
  }

  const ip = details.ipAddress || 'Unknown';

  switch (action) {
    case 'USER_LOGIN':
      return `User logged in from IP: ${ip}`;
    case 'USER_REGISTER':
      return `Registered new account: "${details.name}" (${details.email}) with role "${details.role}" from IP: ${ip}`;
    case 'USER_UPDATE':
      return `Updated user: ${JSON.stringify(details.updatedFields || {}, null, 0)} from IP: ${ip}`;
    case 'READ_BOOK':
      return `Viewed book: "${details.bookTitle || 'N/A'}" (ID: ${details.bookId}) from IP: ${ip}`;
    case 'DOWNLOAD_PDF':
      return `Downloaded PDF: "${details.bookTitle || 'N/A'}" (ID: ${details.bookId}, Version: ${details.versionLabel}) from IP: ${ip}`;
    case 'DOWNLOAD_ZIP':
      return `Downloaded ZIP: "${details.bookTitle || 'N/A'}" (ID: ${details.bookId}, Version: ${details.versionLabel}) from IP: ${ip}`;
    case 'DOWNLOAD_COVER':
      return `Downloaded cover for: "${details.bookTitle || 'N/A'}" (ID: ${details.bookId}) from IP: ${ip}`;
    case 'REQUEST_ACCESS':
      return `Requested access to book: "${details.bookTitle || 'N/A'}" (ID: ${details.bookId}) from IP: ${ip}`;
    case 'SUBMIT_REVIEW':
      return `Submitted review for: "${details.bookTitle || 'N/A'}" (ID: ${details.bookId}) - Subject: "${details.reviewSubject}" from IP: ${ip}`;
    default:
      return `Performed action: ${action} with details: ${JSON.stringify(details, null, 2)} from IP: ${ip}`;
  }
};

/** Dot/chip tone using redesign CSS vars (--mint / --peach / --primary). */
function actionTone(action) {
  const key = (action || '').toUpperCase();
  if (key === 'USER_LOGIN') return 'mint';
  if (key === 'USER_UPDATE') return 'peach';
  if (key === 'SUBMIT_REVIEW') return 'primary';
  if (key === 'USER_REGISTER') return 'lilac';
  if (key.includes('DOWNLOAD') || key === 'READ_BOOK') return 'sky';
  if (key === 'REQUEST_ACCESS') return 'rose';
  return 'lilac';
}

function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [searchType, setSearchType] = useState('all');
  const [filteredLogs, setFilteredLogs] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(50);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const response = await axios.get('/admin/activity-logs', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLogs(response.data);
      } catch (err) {
        console.error('Failed to fetch activity logs:', err);
        setError('Could not load activity logs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  useEffect(() => {
    const filtered = logs.filter((log) => {
      const searchTermLower = searchTerm.toLowerCase().trim();
      const logDate = new Date(log.created_at);
      const logAction = log.action?.toLowerCase() || '';
      const formattedLogDate = logDate
        .toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
        .toLowerCase();

      switch (searchType) {
        case 'user':
          return (
            log.user_name?.toLowerCase().includes(searchTermLower) ||
            log.user_email?.toLowerCase().includes(searchTermLower)
          );
        case 'action':
          return logAction.includes(searchTermLower);
        case 'date': {
          const monthNames = [
            'january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december',
          ];
          const logMonth = logDate.toLocaleString('en-US', { month: 'long' }).toLowerCase();
          const logDay = logDate.getDate();
          const logYear = logDate.getFullYear();

          const fullDateMatch = searchTermLower.match(/([a-z]+)\s+(\d{1,2}),?\s*(\d{4})?/);
          if (fullDateMatch) {
            const [, month, day, year] = fullDateMatch;
            if (monthNames.includes(month)) {
              if (year) {
                return logMonth === month && logDay === parseInt(day, 10) && logYear === parseInt(year, 10);
              }
              return logMonth === month && logDay === parseInt(day, 10);
            }
          }
          if (monthNames.includes(searchTermLower)) {
            return logMonth === searchTermLower;
          }
          return formattedLogDate.includes(searchTermLower);
        }
        default:
          return (
            log.user_name?.toLowerCase().includes(searchTermLower) ||
            log.user_email?.toLowerCase().includes(searchTermLower) ||
            logAction.includes(searchTermLower) ||
            formattedLogDate.includes(searchTermLower)
          );
      }
    });
    setFilteredLogs(filtered);
    setCurrentPage(1);
  }, [logs, searchTerm, searchType]);

  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);

  return (
    <div className="page-wrapper">
      <div className="activity-log-container">
        <div className="page-head">
          <div>
            <h1 className="page-title">User Activity Log</h1>
            <p className="page-desc">
              A running record of logins, edits and submissions across the console.
            </p>
          </div>
        </div>

        <div className="panel" style={{ marginBottom: 18 }}>
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchType={searchType}
            setSearchType={setSearchType}
            timezone={timezone}
            setTimezone={setTimezone}
          />
        </div>

        <div className="panel">
          {loading ? (
            <div className="empty-note">Loading logs…</div>
          ) : error ? (
            <div className="empty-note al-error">{error}</div>
          ) : logs.length === 0 ? (
            <div className="empty-note">No activity logs found.</div>
          ) : filteredLogs.length === 0 ? (
            <div className="empty-note">No logs match your filters.</div>
          ) : (
            <div className="table-wrap">
              <table className="al-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Details</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLogs.map((log, index) => {
                    const tone = actionTone(log.action);
                    return (
                      <tr key={log.id || `${log.created_at}-${index}`}>
                        <td>{indexOfFirstLog + index + 1}</td>
                        <td>
                          <div className="cell-title">{log.user_name || 'N/A'}</div>
                          <div className="cell-sub">{log.user_email || 'N/A'}</div>
                        </td>
                        <td>
                          <span className={`chip chip-${tone}`}>
                            {(log.action || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>
                          <div className="al-details">{formatLogDetails(log.action, log.details)}</div>
                        </td>
                        <td>
                          <div className="al-timestamp">
                            {getTimezoneFormattedDate(log.created_at, timezone)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalCount={filteredLogs.length}
          pageSize={logsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
}

export default ActivityLog;
